/**
 * PersonaDeepDive — Full-screen overlay for 1-on-1 persona chat
 */
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Send, Loader2, Brain, MessageCircle,
  Target, Lightbulb, AlertTriangle, Check, X, Pencil
} from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

// ─── Main Component ────────────────────────────────────────────────────────────

export function PersonaDeepDive({ personaChat, persona, sessionId, topicDimensions = [], config }) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [proposalInput, setProposalInput] = useState('');
  const [showProposalInput, setShowProposalInput] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'reverse' | 'proposal'
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [personaChat.chatMessages, personaChat.streamingContent]);

  useEffect(() => {
    if (!personaChat.isStreaming && inputRef.current) {
      inputRef.current.focus();
    }
  }, [personaChat.isStreaming]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!inputValue.trim() || personaChat.isStreaming) return;
    personaChat.sendMessage(sessionId, persona.id, inputValue);
    setInputValue('');
  };

  const handleReversePersuade = () => {
    setActiveTab('reverse');
    personaChat.reversePersuade(sessionId, persona.id);
  };

  const handleTestProposal = () => {
    if (!proposalInput.trim()) return;
    setActiveTab('proposal');
    personaChat.testProposal(sessionId, persona.id, proposalInput);
    setShowProposalInput(false);
  };

  const dims = personaChat.topicDimensions || topicDimensions || [];
  const hasDimensions = dims.length > 0;

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed inset-0 z-50 bg-stone-50 overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-6 py-4 flex items-center gap-4 flex-shrink-0">
        <button
          onClick={personaChat.closeDeepDive}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">{t('deepDive.backToReport')}</span>
        </button>
        <div className="flex-1 text-center">
          <h1 className="font-serif text-xl text-stone-900">
            {t('deepDive.title')}: {persona.name}
          </h1>
          <p className="text-sm text-stone-500">{config?.topic}</p>
        </div>
        <div className="w-24" /> {/* Spacer for centering */}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel — Position Summary + Live Beliefs */}
        <div className="w-80 lg:w-96 border-r border-stone-200 bg-white overflow-y-auto flex-shrink-0">
          <div className="p-6 space-y-6">
            <PositionSummaryCard
              summary={personaChat.positionSummary}
              persona={persona}
              t={t}
            />
            {hasDimensions ? (
              <LiveBeliefTracker
                beliefs={personaChat.currentBeliefs}
                dimensions={dims}
                positionSummary={personaChat.positionSummary}
                t={t}
              />
            ) : (
              <BasicConvictionBar
                beliefs={personaChat.currentBeliefs}
                positionSummary={personaChat.positionSummary}
                t={t}
              />
            )}
          </div>
        </div>

        {/* Right Panel — Chat + Actions */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tab bar */}
          <div className="flex border-b border-stone-200 bg-white px-4">
            <TabButton active={activeTab === 'chat'} onClick={() => setActiveTab('chat')} icon={MessageCircle} label={t('deepDive.chatTab')} />
            <TabButton
              active={activeTab === 'reverse'}
              onClick={() => setActiveTab('reverse')}
              icon={Lightbulb}
              label={t('deepDive.reversePersuade')}
              badge={personaChat.reversePersuadeResult ? '3' : null}
            />
            <TabButton
              active={activeTab === 'proposal'}
              onClick={() => setActiveTab('proposal')}
              icon={Target}
              label={t('deepDive.testProposal')}
              badge={personaChat.proposalResult ? '1' : null}
            />
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {activeTab === 'chat' && (
              <>
                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {personaChat.chatMessages.length === 0 && !personaChat.isStreaming && (
                    <div className="text-center py-12 text-stone-400">
                      <Brain className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                      <p className="text-lg">{t('deepDive.emptyState')}</p>
                      <p className="text-sm mt-1">{t('deepDive.emptyStateHint')}</p>
                    </div>
                  )}
                  {personaChat.chatMessages.map((msg, idx) => (
                    <ChatMessage key={idx} message={msg} personaName={persona.name} />
                  ))}
                  {personaChat.isStreaming && personaChat.streamingContent && activeTab === 'chat' && (
                    <ChatMessage
                      message={{ role: 'persona', content: personaChat.streamingContent }}
                      personaName={persona.name}
                      isStreaming
                    />
                  )}
                  {personaChat.isStreaming && !personaChat.streamingContent && activeTab === 'chat' && (
                    <div className="flex items-center gap-2 text-stone-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{t('deepDive.thinking')}</span>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Input */}
                <div className="border-t border-stone-200 bg-white p-4">
                  <form onSubmit={handleSend} className="flex gap-3">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={t('deepDive.inputPlaceholder')}
                      className="flex-1 px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      disabled={personaChat.isStreaming}
                    />
                    <button
                      type="submit"
                      disabled={!inputValue.trim() || personaChat.isStreaming}
                      className="px-4 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </form>

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleReversePersuade}
                      disabled={personaChat.isStreaming}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors"
                    >
                      <Lightbulb className="w-3.5 h-3.5" />
                      {t('deepDive.reversePersuade')}
                    </button>
                    <button
                      onClick={() => setShowProposalInput(!showProposalInput)}
                      disabled={personaChat.isStreaming}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 disabled:opacity-50 transition-colors"
                    >
                      <Target className="w-3.5 h-3.5" />
                      {t('deepDive.testProposal')}
                    </button>
                  </div>

                  {/* Proposal input (inline) */}
                  <AnimatePresence>
                    {showProposalInput && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="flex gap-2 mt-2">
                          <input
                            type="text"
                            value={proposalInput}
                            onChange={(e) => setProposalInput(e.target.value)}
                            placeholder={t('deepDive.proposalPlaceholder')}
                            className="flex-1 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                            onKeyDown={(e) => e.key === 'Enter' && handleTestProposal()}
                          />
                          <button
                            onClick={handleTestProposal}
                            disabled={!proposalInput.trim()}
                            className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
                          >
                            {t('deepDive.send')}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}

            {activeTab === 'reverse' && (
              <div className="flex-1 overflow-y-auto p-6">
                <ReversePersuadePanel
                  result={personaChat.reversePersuadeResult}
                  isStreaming={personaChat.isStreaming}
                  streamingContent={personaChat.streamingContent}
                  onStart={handleReversePersuade}
                  t={t}
                />
              </div>
            )}

            {activeTab === 'proposal' && (
              <div className="flex-1 overflow-y-auto p-6">
                <ProposalTestPanel
                  result={personaChat.proposalResult}
                  isStreaming={personaChat.isStreaming}
                  streamingContent={personaChat.streamingContent}
                  t={t}
                />
                {!personaChat.proposalResult && !personaChat.isStreaming && (
                  <div className="mt-6">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={proposalInput}
                        onChange={(e) => setProposalInput(e.target.value)}
                        placeholder={t('deepDive.proposalPlaceholder')}
                        className="flex-1 px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
                        onKeyDown={(e) => e.key === 'Enter' && handleTestProposal()}
                      />
                      <button
                        onClick={handleTestProposal}
                        disabled={!proposalInput.trim()}
                        className="px-4 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 disabled:opacity-50"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error banner */}
      <AnimatePresence>
        {personaChat.error && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-100 text-red-700 px-6 py-3 rounded-xl shadow-lg flex items-center gap-2"
          >
            <AlertTriangle className="w-4 h-4" />
            {personaChat.error}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}


// ─── Sub-Components ─────────────────────────────────────────────────────────────

function TabButton({ active, onClick, icon: Icon, label, badge }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-amber-600 text-amber-700'
          : 'border-transparent text-stone-500 hover:text-stone-700'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
      {badge && (
        <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
          {badge}
        </span>
      )}
    </button>
  );
}


function PositionSummaryCard({ summary, persona, t }) {
  if (!summary) return null;

  const trajectoryColors = {
    Anchor: 'bg-stone-100 text-stone-700',
    Drifter: 'bg-blue-100 text-blue-700',
    Convert: 'bg-purple-100 text-purple-700',
    Polarizer: 'bg-red-100 text-red-700',
    Bridger: 'bg-green-100 text-green-700',
    Explorer: 'bg-amber-100 text-amber-700',
  };

  return (
    <div>
      <h3 className="font-serif text-lg text-stone-900 mb-3">{t('deepDive.positionSummary')}</h3>
      <div className="space-y-3">
        {/* Trajectory badge */}
        {summary.trajectory && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-stone-500">{t('deepDive.trajectory')}:</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${trajectoryColors[summary.trajectory] || 'bg-stone-100 text-stone-600'}`}>
              {summary.trajectory}
            </span>
          </div>
        )}

        {/* Conviction */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-stone-500">{t('deepDive.conviction')}</span>
            <span className="font-medium">{((summary.conviction || 0.5) * 100).toFixed(0)}%</span>
          </div>
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-amber-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(summary.conviction || 0.5) * 100}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Emotional stance */}
        {summary.emotional_stance && (
          <div className="text-sm">
            <span className="text-stone-500">{t('deepDive.finalStance')}:</span>{' '}
            <span className="text-stone-700">{summary.emotional_stance}</span>
          </div>
        )}

        {/* Key positions */}
        {summary.key_positions && summary.key_positions.length > 0 && (
          <div>
            <span className="text-sm text-stone-500">{t('deepDive.keyPositions')}:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {summary.key_positions.map((pos, i) => (
                <span key={i} className="px-2 py-0.5 bg-stone-100 rounded text-xs text-stone-700">
                  {pos}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Agreements / Disagreements */}
        {summary.agreements && Object.keys(summary.agreements).length > 0 && (
          <div className="text-sm">
            <span className="text-green-600">{t('deepDive.agreedWith')}:</span>{' '}
            <span className="text-stone-600">{Object.keys(summary.agreements).join(', ')}</span>
          </div>
        )}
        {summary.disagreements && Object.keys(summary.disagreements).length > 0 && (
          <div className="text-sm">
            <span className="text-red-600">{t('deepDive.disagreedWith')}:</span>{' '}
            <span className="text-stone-600">{Object.keys(summary.disagreements).join(', ')}</span>
          </div>
        )}
      </div>
    </div>
  );
}


function LiveBeliefTracker({ beliefs, dimensions, positionSummary, t }) {
  const dimScores = beliefs?.dimensions || beliefs?.dimension_scores || positionSummary?.dimension_scores || {};

  return (
    <div>
      <h3 className="font-serif text-lg text-stone-900 mb-3">{t('deepDive.liveBeliefs')}</h3>
      {Object.keys(dimScores).length === 0 ? (
        <p className="text-sm text-stone-400">{t('deepDive.noBeliefData')}</p>
      ) : (
        <div className="space-y-3">
          {Object.entries(dimScores).map(([dimId, dimData]) => {
            const score = typeof dimData === 'object'
              ? (dimData.score ?? dimData.score ?? 0.5)
              : dimData;
            const label = typeof dimData === 'object' && dimData.label
              ? dimData.label
              : dimensions.find(d => d.id === dimId)?.label || dimId;
            const poleLow = dimensions.find(d => d.id === dimId)?.pole_low || '';
            const poleHigh = dimensions.find(d => d.id === dimId)?.pole_high || '';

            return (
              <div key={dimId}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-stone-600 font-medium">{label}</span>
                  <span className="text-stone-400">{(score * 100).toFixed(0)}%</span>
                </div>
                <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-purple-500 rounded-full"
                    animate={{ width: `${score * 100}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
                {(poleLow || poleHigh) && (
                  <div className="flex justify-between text-[10px] text-stone-400 mt-0.5">
                    <span>{poleLow}</span>
                    <span>{poleHigh}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function BasicConvictionBar({ beliefs, positionSummary, t }) {
  const conviction = beliefs?.conviction_level || beliefs?.overall_conviction || positionSummary?.conviction || 0.5;
  const positions = beliefs?.key_positions || positionSummary?.key_positions || [];

  return (
    <div>
      <h3 className="font-serif text-lg text-stone-900 mb-3">{t('deepDive.liveBeliefs')}</h3>
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-stone-500">{t('deepDive.conviction')}</span>
          <span className="font-medium">{(conviction * 100).toFixed(0)}%</span>
        </div>
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-amber-500 rounded-full"
            animate={{ width: `${conviction * 100}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>
      {positions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {positions.map((pos, i) => (
            <span key={i} className="px-2 py-0.5 bg-stone-100 rounded text-xs text-stone-700">{pos}</span>
          ))}
        </div>
      )}
    </div>
  );
}


function ChatMessage({ message, personaName, isStreaming }) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] px-4 py-3 rounded-2xl ${
          isUser
            ? 'bg-amber-600 text-white rounded-br-sm'
            : 'bg-white border border-stone-200 text-stone-800 rounded-bl-sm shadow-sm'
        }`}
      >
        {!isUser && (
          <div className="text-xs font-medium text-purple-600 mb-1">{personaName}</div>
        )}
        <div className="text-sm leading-relaxed whitespace-pre-wrap">
          {message.content}
          {isStreaming && (
            <span className="inline-block w-1.5 h-4 bg-purple-400 ml-0.5 animate-pulse rounded" />
          )}
        </div>
      </div>
    </div>
  );
}


function ReversePersuadePanel({ result, isStreaming, streamingContent, onStart, t }) {
  if (!result && !isStreaming) {
    return (
      <div className="text-center py-12">
        <Lightbulb className="w-12 h-12 mx-auto mb-3 text-purple-300" />
        <p className="text-lg text-stone-600 mb-2">{t('deepDive.reversePersuadeTitle')}</p>
        <p className="text-sm text-stone-400 mb-6 max-w-md mx-auto">{t('deepDive.reversePersuadeDesc')}</p>
        <button
          onClick={onStart}
          className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors"
        >
          {t('deepDive.reversePersuadeStart')}
        </button>
      </div>
    );
  }

  if (isStreaming) {
    return (
      <div>
        <div className="flex items-center gap-2 text-purple-600 mb-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm font-medium">{t('deepDive.thinking')}</span>
        </div>
        {streamingContent && (
          <div className="bg-white rounded-xl p-4 border border-stone-200 text-sm text-stone-700 whitespace-pre-wrap">
            {streamingContent}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="font-serif text-xl text-stone-900">{t('deepDive.vulnerabilities')}</h3>
      {result.vulnerabilities && result.vulnerabilities.length > 0 ? (
        result.vulnerabilities.map((v, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-white rounded-xl p-5 border border-purple-200 shadow-sm"
          >
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-bold">
                {idx + 1}
              </span>
              <div className="flex-1">
                <p className="font-medium text-stone-900 mb-2">{v.argument}</p>
                {v.target_dimension && (
                  <span className="inline-block px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs mb-2">
                    {t('deepDive.targets')}: {v.target_dimension}
                  </span>
                )}
                {v.estimated_impact && (
                  <span className={`inline-block ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                    v.estimated_impact === 'high' ? 'bg-red-100 text-red-700' :
                    v.estimated_impact === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {v.estimated_impact}
                  </span>
                )}
                {v.reasoning && (
                  <p className="text-sm text-stone-600 mt-2 italic">{v.reasoning}</p>
                )}
              </div>
            </div>
          </motion.div>
        ))
      ) : (
        <div className="bg-white rounded-xl p-4 border border-stone-200 text-sm text-stone-700 whitespace-pre-wrap">
          {result.raw}
        </div>
      )}
    </div>
  );
}


function ProposalTestPanel({ result, isStreaming, streamingContent, t }) {
  if (!result && !isStreaming) {
    return (
      <div className="text-center py-12">
        <Target className="w-12 h-12 mx-auto mb-3 text-amber-300" />
        <p className="text-lg text-stone-600 mb-2">{t('deepDive.proposalTestTitle')}</p>
        <p className="text-sm text-stone-400 max-w-md mx-auto">{t('deepDive.proposalTestDesc')}</p>
      </div>
    );
  }

  if (isStreaming) {
    return (
      <div>
        <div className="flex items-center gap-2 text-amber-600 mb-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm font-medium">{t('deepDive.thinking')}</span>
        </div>
        {streamingContent && (
          <div className="bg-white rounded-xl p-4 border border-stone-200 text-sm text-stone-700 whitespace-pre-wrap">
            {streamingContent}
          </div>
        )}
      </div>
    );
  }

  const verdictStyles = {
    accept: { bg: 'bg-green-100', text: 'text-green-700', icon: Check, label: t('deepDive.accept') },
    reject: { bg: 'bg-red-100', text: 'text-red-700', icon: X, label: t('deepDive.reject') },
    modify: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Pencil, label: t('deepDive.modify') },
  };
  const style = verdictStyles[result.verdict] || verdictStyles.modify;
  const VerdictIcon = style.icon;

  return (
    <div className="space-y-4">
      {/* Verdict Badge */}
      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-2 px-4 py-2 ${style.bg} ${style.text} rounded-xl text-lg font-medium`}>
          <VerdictIcon className="w-5 h-5" />
          {style.label}
        </div>
      </div>

      {/* Reasoning */}
      {result.reasoning && (
        <div className="bg-white rounded-xl p-5 border border-stone-200">
          <h4 className="text-sm font-medium text-stone-500 mb-2">{t('deepDive.reasoning')}</h4>
          <p className="text-stone-800">{result.reasoning}</p>
        </div>
      )}

      {/* Modifications */}
      {result.verdict === 'modify' && result.suggested_modifications && (
        <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
          <h4 className="text-sm font-medium text-amber-700 mb-2">{t('deepDive.suggestedModifications')}</h4>
          <p className="text-stone-800">{result.suggested_modifications}</p>
        </div>
      )}

      {/* Dimension Impacts */}
      {result.dimension_impacts && Object.keys(result.dimension_impacts).length > 0 && (
        <div className="bg-white rounded-xl p-5 border border-stone-200">
          <h4 className="text-sm font-medium text-stone-500 mb-3">{t('deepDive.dimensionImpact')}</h4>
          <div className="space-y-2">
            {Object.entries(result.dimension_impacts).map(([dim, impact]) => (
              <div key={dim} className="flex items-center gap-2">
                <span className={`text-lg ${impact.direction === '+' ? 'text-green-500' : 'text-red-500'}`}>
                  {impact.direction === '+' ? '+' : '-'}
                </span>
                <span className="text-sm font-medium text-stone-700">{dim}</span>
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  impact.magnitude === 'high' ? 'bg-red-100 text-red-700' :
                  impact.magnitude === 'medium' ? 'bg-amber-100 text-amber-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {impact.magnitude}
                </span>
                {impact.explanation && (
                  <span className="text-xs text-stone-500">{impact.explanation}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
